import * as React from 'react'
import {Lang} from 'yougile-lang'
import {logger, LongId, Util, UUID} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  sharingGuestAccount,
  User,
  youGileUserId
} from 'yougile-model'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {SharedBoardsHelper} from '@client/shared-boards-helper'
import {PageService} from '@extensions/page-service'
import {Accounts} from '../client/accounts'
import {PaymentClient} from '../client/payment-client'
import {Feedback} from '../extensions/feedback'
import {globals} from '../extensions/globals-base'
import {Search} from '../extensions/search'

interface UsernameProps {
  user?: User
  userId?: DataObjectId
  task?: Hub
  limit?: number
  highlight?: string
  guestId?: string
  guestName?: string
  preTitle?: string
}

type Props = UsernameProps & React.HTMLProps<Element>

export const getUserAccountName = (
  userLike: {id: DataObjectId},
  task?: Hub
): string => {
  if (!userLike) {
    return ''
  }
  const userId = userLike.id
  if (userId === sharingGuestAccount.id) {
    return Lang.msg.share.guest
  }
  let realName = Accounts.getName(userId, undefined, true)

  if (!realName || realName === '_') {
    if (userId === youGileUserId) {
      realName = 'YouGile Team'
    }
    if (task && !!Feedback.column && task.location === Feedback.column.id) {
      realName = 'YouGile support'
    } else if (
      task &&
      Feedback.getFeedbackUserData(task) &&
      Feedback.getFeedbackUserData(task).name
    ) {
      realName = Feedback.getFeedbackUserData(task).name
    }
  }
  return realName
}

export class UserName extends React.PureComponent<Props> {
  private updateHandlerId: UUID
  private paymentUpdateHandlerId: LongId
  private element: HTMLElement
  private triedReloadGuestId: string

  private getName(): string {
    const {task, guestId} = this.props
    if (guestId) {
      const info = SharedBoardsClient.getGuestInfo(guestId)
      if (!info && !this.triedReloadGuestId) {
        const boardId = PageService.getCurrentBoard()?.id
        if (boardId) {
          this.triedReloadGuestId = guestId
          SharedBoardsHelper.updateGustsInfo(boardId)
            .then(() => {
              this.forceUpdate()
            })
            .catch(e => logger.error(e))
        }
      }
      return info?.name
    } else {
      const userId = this.props.user?.id ?? this.props.userId
      return getUserAccountName({id: userId}, task)
    }
  }

  constructor(props: Props) {
    super(props)
    this.state = {}
  }

  public override componentDidMount(): void {
    const userId = this.props.userId || this.props.user?.id
    if (!userId) {
      return
    }
    const update = () => {
      if (!this.props.user && !this.props.userId) {
        logger.warn('No user at element', this.element)
        return
      }
      this.forceUpdate()
    }
    this.updateHandlerId = Accounts.addListener({account: userId, cb: update})
    this.paymentUpdateHandlerId = PaymentClient.addListener(_companyId => {
      update()
    })
  }

  public override componentWillUnmount(): void {
    if (this.updateHandlerId) {
      Accounts.removeListener(this.updateHandlerId)
    }
    PaymentClient.removeListener(this.paymentUpdateHandlerId)
  }

  // eslint-disable-next-line max-lines-per-function
  public override render(): React.ReactNode {
    if (!this.props.user && !this.props.userId) {
      return null
    }
    const {
      user,
      userId,
      limit: _,
      highlight,
      title,
      task: _task,
      guestId: _guestId,
      guestName: _guestName,
      preTitle = '',
      ...domProps
    } = this.props

    const id = user?.id ?? userId
    const realName = this.getName()
    const realNameDisplayed = realName
    const ref = (el: HTMLElement) => (this.element = el)
    const notRegistered = Util.isEmail(realNameDisplayed)
    const isDisallowed = PaymentClient.isUserDisallowed(
      globals.getCompanyId(),
      userId || user.id
    )
    const className = [
      domProps.className ?? '',
      'user-name',
      notRegistered ? 'not-registered' : '',
      isDisallowed ? 'disallowed' : ''
    ].join(' ')

    const bem = Util.bemFrom('user-name')
    if (highlight) {
      const parts: JSX.Element[] = []
      let i = 0
      Search.highlight(
        highlight,
        realNameDisplayed,
        text => {
          i++
          parts.push(
            <span key={i} className={bem('plain')}>
              {text}
            </span>
          )
        },
        hl => {
          i++
          parts.push(
            <span key={i} className={bem('highlighted')}>
              {hl}
            </span>
          )
        }
      )
      return (
        <span
          {...domProps}
          className={className}
          title={title ?? realName}
          ref={ref}
        >
          {parts}
        </span>
      )
    }

    const composedTitle = preTitle + (title ? title : realName)

    return (
      <span {...domProps} className={className} title={composedTitle} ref={ref}>
        {realNameDisplayed}
        {id === sharingGuestAccount.id &&
          (realName ? (
            <span className={bem('guest')}>({Lang.msg.share.guest})</span>
          ) : (
            <span className={bem('guest', {noName: true})}>
              {Lang.msg.share.guest}
            </span>
          ))}
      </span>
    )
  }
}
